import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Edit3, MoreVertical, Trash2, Loader2, RotateCcw } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AssessmentForm, AssessmentFormSchema } from "@/lib/schemas";
import { listAssessments, createAssessment, updateAssessment, deleteAssessment, reactivateAssessment, type Assessment } from "@/integrations/backend/assessments";

export default function AssessmentsView() {
  const queryClient = useQueryClient();
  const [showDeleted, setShowDeleted] = useState(false);

  const assessmentListKey = queryKeys.assessments.adminList(showDeleted);
  const assessmentsQuery = useQuery({
    queryKey: assessmentListKey,
    queryFn: () => listAssessments({ include_deleted: showDeleted }),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...assessmentListKey]),
  });

  const assessments = useMemo(() => {
    const data = assessmentsQuery.data ?? [];
    return data.filter((a) => (showDeleted ? a.is_deleted : !a.is_deleted));
  }, [assessmentsQuery.data, showDeleted]);

  const loading = assessmentsQuery.isLoading;
  const loadError = assessmentsQuery.error instanceof Error ? assessmentsQuery.error.message : assessmentsQuery.error ? String(assessmentsQuery.error) : null;

  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Assessment | null>(null);

  const defaultValues = useMemo<AssessmentForm>(
    () => ({
      name: editing?.name ?? "",
      description: editing?.description ?? "",
      typeform_url: editing?.typeform_url ?? "",
    }),
    [editing]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AssessmentForm>({
    resolver: zodResolver(AssessmentFormSchema),
    defaultValues,
  });

  useEffect(() => {
    // keep form fields in sync when opening for edit/new
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSave = handleSubmit(async (values) => {
    try {
      setError(null);
      if (editing) {
        await updateAssessment(editing.id, values);
      } else {
        await createAssessment(values);
      }
      await queryClient.invalidateQueries({ queryKey: ["assessments", "admin"] });
      reset(defaultValues);
      setOpen(false);
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save assessment");
    }
  });

  const openNew = () => {
    setEditing(null);
    setOpen(true);
  };

  const openEdit = (a: Assessment) => {
    setEditing(a);
    setOpen(true);
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this assessment?")) return;
    try {
      setError(null);
      await deleteAssessment(id);
      await queryClient.invalidateQueries({ queryKey: ["assessments", "admin"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete assessment");
    }
  };

  const reactivate = async (a: Assessment) => {
    if (!confirm(`Reactivate "${a.name}"?`)) return;
    try {
      setError(null);
      await reactivateAssessment(a.id);
      await queryClient.invalidateQueries({ queryKey: ["assessments", "admin"] });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reactivate assessment");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Assessments</h1>
          <p className="text-gray-600">Manage assessment forms your clients will complete.</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button onClick={openNew} disabled={loading}>Add Assessment</Button>
          </DialogTrigger>
          <DialogContent className="bg-white text-black">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit Assessment" : "Add Assessment"}</DialogTitle>
            </DialogHeader>
            <form className="space-y-3 py-1" onSubmit={onSave}>
              <div className="space-y-1">
                <Input
                  placeholder="Name"
                  aria-label="Name"
                  {...register("name")}
                  className="bg-white text-black placeholder:text-gray-500"
                />
                {errors.name?.message && (
                  <p className="text-sm text-destructive">{errors.name.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <Textarea
                  placeholder="Description"
                  aria-label="Description"
                  {...register("description")}
                  className="min-h-[120px] bg-white text-black placeholder:text-gray-500"
                />
                {errors.description?.message && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </div>
              <div className="space-y-1">
                <Input
                  placeholder="Typeform URL"
                  aria-label="Typeform URL"
                  {...register("typeform_url")}
                  className="bg-white text-black placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-600">
                  Paste the public Typeform link (Share → Link), like{" "}
                  <span className="font-mono">https://form.typeform.com/to/&lt;id&gt;</span>. Leave blank if not ready.
                </p>
                {errors.typeform_url?.message && (
                  <p className="text-sm text-destructive">{errors.typeform_url.message}</p>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setOpen(false); setEditing(null); }} className="text-black border-gray-300 hover:bg-gray-100">
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  Save
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-4 border-b border-gray-200 pb-2">
        <button
          onClick={() => setShowDeleted(false)}
          className={`px-4 py-2 font-medium transition-colors ${
            !showDeleted
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Active Assessments
        </button>
        <button
          onClick={() => setShowDeleted(true)}
          className={`px-4 py-2 font-medium transition-colors ${
            showDeleted
              ? "text-primary border-b-2 border-primary"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          Deleted Assessments
        </button>
      </div>

      {(error || loadError) && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4">
          <p className="text-sm text-red-800">{error || loadError}</p>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>All Assessments</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Typeform URL</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assessments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-sm text-gray-500">
                        {showDeleted ? "No deleted assessments." : "No active assessments yet."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    assessments.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.name}</TableCell>
                    <TableCell className="max-w-[420px]">
                      <span className="line-clamp-3">{a.description}</span>
                    </TableCell>
                    <TableCell>
                      <a href={a.typeform_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                        {a.typeform_url}
                      </a>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" aria-label="Actions">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(a)}>
                            <Edit3 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          {a.is_deleted ? (
                            <DropdownMenuItem onClick={() => reactivate(a)}>
                              <RotateCcw className="w-4 h-4 mr-2" /> Reactivate
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => remove(a.id)} className="text-destructive focus:text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


